import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { generateLetterOffsets } from '../00-shared/constants';
import { AnimatedText } from '../00-shared/AnimatedText';

interface PartnershipSectionProps {
    highlightColor: {
        primary: string;
        hover: string;
        gradient: {
            from: string;
            to: string;
            fromBlue: string;
            toPurple: string;
        };
    };
}

const PartnershipSection: React.FC<PartnershipSectionProps> = ({ highlightColor }) => {
    const navigate = useNavigate();
    const [animated, setAnimated] = useState(false);
    const titleRef = useRef<HTMLDivElement>(null);
    
    const titleText = 'Built for coaches, athletes, and performance staff.';
    const subtitleText = 'Our platform serves teams across all levels – from collegiate athletics to professional ' + 
    'sports, helping coaches optimize performance and prevent injuries.';
    
    const titleOffsets = useMemo(() => generateLetterOffsets(titleText, 100, 200), []);
    const subtitleOffsets = useMemo(() => generateLetterOffsets(subtitleText, 100, 100), []);
    
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting && !animated) {
                        setAnimated(true);
                    }
                });
            },
            { threshold: 0.1 }
        );

        if (titleRef.current) {
            observer.observe(titleRef.current);
        }

        return () => observer.disconnect();
    }, [animated]);
    
    const coachFeatures = [
        'Connect all your data in one place — WHOOP, Catapult, VALD, Hawkins, and more.',
        'See clear, team-specific insights based on your training schedule, testing data, and athlete profiles.',
        'Track readiness, recovery, and workload trends in real time so you can adjust plans with confidence.'
    ];

    const athleteFeatures = [
        'Connect all your wearables in one place so you can see recovery, readiness, and activity trends without juggling apps.',
        'Get clear, actionable insights that help you guide training loads, monitor progress, and spot early signs of fatigue or overuse.',
        'Give clients an elevated, sport-science experience that improves engagement, accountability, and long-term retention.'
    ];

    return (
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-black relative">
            <div className="max-w-7xl mx-auto relative z-10">
                <div className="text-center pb-16" ref={titleRef}>
                    <AnimatedText
                        as="h2"
                        text={titleText}
                        offsets={titleOffsets}
                        animated={animated}
                        className="text-3xl md:text-5xl text-white mb-4"
                    />
                    <div className="text-lg text-gray-300 max-w-3xl mx-auto">
                        <AnimatedText
                            text={subtitleText}
                            offsets={subtitleOffsets}
                            animated={animated}
                        />
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {/* For Coaches */}
                    <div className={`bg-neutral-900 rounded-xl p-10 pt-8 observe-fade opacity-0 border border-neutral-800 flex flex-col h-full`}>
                        <div className="flex-grow">
                            <div className="flex items-center justify-center">
                                <img
                                    src={'/images/home-partnership-dashboard.png'}
                                    className="w-auto h-28 object-cover"
                                />
                            </div>
                            <h3 className="text-center text-2xl font-bold text-white mb-6">
                                Sports Programs
                            </h3>
                            <p className="text-center text-gray-300 mb-6">
                                A single system that helps performance staff organize data, monitor athletes, and make informed decisions without adding extra work.
                            </p>
                            <ul className="space-y-3 text-gray-300">
                                {coachFeatures.map((feature, index) => (
                                    <li key={index} className="flex items-start">
                                        <span className="text-green-400 mr-3 text-xl">•</span>
                                        <span>{feature}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className="mt-auto pt-8">
                            <button 
                                onClick={() => navigate('/contact')}
                                className="w-full py-3 text-white rounded-lg transition-colors font-medium"
                                style={{ backgroundColor: highlightColor.primary }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = highlightColor.hover;
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = highlightColor.primary;
                                }}
                            >
                                GET STARTED
                            </button>
                        </div>
                    </div>
                    {/* For Athletes */}
                    <div className={`bg-neutral-900 rounded-xl p-10 pt-8 observe-fade opacity-0 border border-neutral-800 flex flex-col h-full`}>
                        <div className="flex-grow">
                            <div className="flex items-center justify-center">
                                <img
                                    src={'/images/services-analytics-platform.png'}
                                    className="w-auto h-28 object-cover"
                                />
                            </div>
                            <h3 className="text-center text-2xl font-bold text-white mb-6">
                                Health and Wellness
                            </h3>
                            <p className="text-center text-gray-300 mb-6">
                                Gain the same level of performance clarity used by elite sports programs and apply it to your clients’ everyday training and recovery.
                            </p>
                            <ul className="space-y-3 text-gray-300">
                                {athleteFeatures.map((feature, index) => (
                                    <li key={index} className="flex items-start">
                                        <span className="text-green-400 mr-3 text-xl">•</span>
                                        <span>{feature}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className="mt-auto pt-8">
                            <button 
                                onClick={() => navigate('/register')}
                                className="w-full py-3 text-white rounded-lg transition-colors font-medium"
                                style={{ backgroundColor: highlightColor.primary }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = highlightColor.hover;
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = highlightColor.primary;
                                }}
                            >
                                GET STARTED
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default PartnershipSection;

